import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Field, FieldInput, FieldLabel } from '../components/Field';
import Loader from '../components/Loader';
import { A } from '../components/Markdown';
import { Modal, ModalExit, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { useTheme } from 'styled-components';
import { VIEWER } from '../utilities/queries';
import { DELETE_VIEWER } from '../utilities/mutations';
import { getHome } from '../utilities/getHome';

const DeleteAccountModal = () => {
  const navigate = useNavigate();
  const { loading: viewerLoading, data: viewerData } = useQuery(VIEWER);
  const [deleteViewer] = useMutation(DELETE_VIEWER);
  const toast = useToast();
  const theme = useTheme();

  return (
    <Modal onClose={() => navigate('/settings/data')} isDanger>
      {viewerLoading && !viewerData ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          {viewerData.viewer.spaces.length !== 0 && <ModalExit />}
          <ModalName>Delete account</ModalName>
          {viewerData.viewer.spaces.length === 0 ? (
            <Formik
              initialValues={{ name: '' }}
              onSubmit={async (values) => {
                if (values.name !== viewerData.viewer.name) {
                  toast({ children: 'Name is invalid' });
                } else {
                  try {
                    await deleteViewer();
                    localStorage.removeItem('token');
                    getHome(navigate);
                  } catch (error) {
                    toast({ children: error.message });
                  }
                }
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
              }) => (
                <form onSubmit={handleSubmit}>
                  <ModalPart>
                    <div
                      style={{
                        color: theme.alias.subtle,
                        lineHeight: '20px',
                        fontWeight: 500,
                      }}
                    >
                      This will instantly delete your account and all of its
                      data forever.
                    </div>
                    <Field>
                      <FieldLabel>
                        Confirm your name ({viewerData.viewer.name})
                      </FieldLabel>
                      <FieldInput
                        type="text"
                        name="name"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.name}
                      />
                    </Field>
                  </ModalPart>
                  <ModalPart>
                    <Button
                      type="button"
                      onClick={() => navigate('/settings/data')}
                      isExtended
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        isSubmitting || values.name !== viewerData.viewer.name
                      }
                      isDanger
                      isExtended
                    >
                      Delete
                    </Button>
                  </ModalPart>
                </form>
              )}
            </Formik>
          ) : (
            <ModalPart>
              <div
                style={{
                  color: theme.alias.subtle,
                  lineHeight: '20px',
                  fontWeight: 500,
                }}
              >
                To delete your account, you must be in no spaces.
              </div>
              <div
                style={{
                  lineHeight: '20px',
                }}
              >
                If you're an owner, you can transfer ownership or delete the
                space. If you're an admin or user, you can{' '}
                <A href="/settings/spaces">leave</A> the space. When you are in
                no spaces,{' '}
                <A href="/settings/data/delete-account" isExternal>
                  reload
                </A>{' '}
                this page to delete your account.
              </div>
            </ModalPart>
          )}
        </>
      )}
    </Modal>
  );
};

export default DeleteAccountModal;
